import { AbstractControl, FormArray, FormRecord, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { RiskParamForm } from '@app/features/main/views/risks/risks-import/risks-import.component';
import { isPresent } from '@app/shared/validators/extra-validators.module';

/**
 * Verify that all the values in the list are unique
 * @param list
 */
export const distinct = (list: any[]) => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isPresent(Validators.required(control))) return null;

    return list.includes(control.value) ? {distinct: true} : null;
  };
};

/***
 * Check if the value of each control of the array is unique
 */
export const distinctRiskFieldValueOrders = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control || !control.parent || isPresent(Validators.required(control))) {
      return null;
    }
    const fieldValue = control.value;
    const controlArray = control.parent as FormArray;
    const fieldValueOccurrence = controlArray.controls.filter(control => control.value == fieldValue).length;
    if (fieldValueOccurrence < 2) {
      controlArray.controls.filter(control => control.invalid).forEach(control => control.updateValueAndValidity());
      return null;
    } else {
      return {unique: true};
    }
  };
};

export const distinctRiskParamFields = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control || !control.parent?.parent || isPresent(Validators.required(control))) {
      return null;
    }
    const newValue = control.value?.code;
    const paramGroupFrom = control.parent.parent as FormRecord<RiskParamForm>;
    const fieldControls = Object.values(paramGroupFrom.controls).map(param => param.controls.field);
    const fieldOccurrence = fieldControls.filter(param => param.value?.code == newValue).length;
    if (fieldOccurrence < 2) {
      fieldControls.filter(control => control.invalid).forEach(control => control.updateValueAndValidity());
      return null;
    } else {
      return {distinctRiskParamFields: true};
    }
  };
};

/**
 * Check if the parent controls criteria value is unique.
 * @param items Existing elements to compare to.
 * @param criteria Criteria names.
 */
export const distinctCriteria = (items: Record<string, string>[] = [], criteria: string[] = []): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray)) return null;
    if (control.controls.some(childControl => childControl.value == null)) return null;

    const duplicateFound = items
      // Filter self
      .filter(item => item.id !== control.at(-1).value)
      // Iterate through each element until one duplicate is found
      .some(item => {
        return criteria.every((criterion, index) => control.at(index).value === item[criterion]);
      });

    if (duplicateFound) {
      // Set error in children form manually
      criteria.forEach((_, index) => control.at(index).setErrors({distinctCriteria: true}));
      return {distinctCriteria: true};
    } else {
      criteria.forEach((_, index) => {
        const errors = control.at(index).errors;
        // Validator is valid, remove the error from children form manually if it was on error.
        if (control.at(index).hasError('distinctCriteria')) delete errors.distinctCriteria;
        control.at(index).setErrors(errors);
        control.at(index).updateValueAndValidity({onlySelf: true});
      });
      return null;
    }
  };
};

